import type {
    QuotationInvoiceDocumentItemDTO,
    QuotationInvoiceDocumentListItem,
    QuotationInvoiceDocumentPaymentDTO,
} from "@/modules/quotation-invoice/domain/types";
import { QuotationInvoiceDocumentBody, type ReceiptPaymentRecord } from "./QuotationInvoiceDocumentBody";

interface DocumentPdfPreviewContentProps {
  document: QuotationInvoiceDocumentListItem;
  items: QuotationInvoiceDocumentItemDTO[];
  payments?: QuotationInvoiceDocumentPaymentDTO[];
}

export function buildReceiptPaymentRecord(
  doc: QuotationInvoiceDocumentListItem,
  payments: QuotationInvoiceDocumentPaymentDTO[],
): ReceiptPaymentRecord | null {
  if (doc.documentType !== "receipt" || !doc.paymentId) return null;
  const payment = payments.find((p) => p.id === doc.paymentId);
  if (!payment) return null;

  function round2(n: number) { return Math.round(n * 100) / 100; }

  // previouslyPaid = sum payments ก่อน payment นี้ (ตาม order ใน array)
  let previouslyPaid = 0;
  for (const p of payments) {
    if (p.id === payment.id) break;
    if (p.kind === "refund") previouslyPaid -= p.amount;
    else previouslyPaid += p.amount;
  }
  previouslyPaid = round2(Math.max(0, previouslyPaid));

  // ใช้ยอดจาก parent invoice ที่ backend JOIN มาให้ตรงๆ
  const invoiceTotal = doc.invoiceTotal ?? Number(doc.total);
  const invoiceSubtotal = doc.invoiceSubtotal ?? Number(doc.subtotal);
  const invoiceDiscountAmount = doc.invoiceDiscountAmount ?? Number(doc.discountAmount);
  const invoiceTaxAmount = doc.invoiceTaxAmount ?? Number(doc.taxAmount);

  const remaining = round2(invoiceTotal - (previouslyPaid + Number(payment.amount)));

  return {
    amount: Number(payment.amount),
    paidAt: payment.paidAt,
    method: payment.method,
    notes: payment.notes,
    previouslyPaid,
    invoiceTotal,
    invoiceSubtotal,
    invoiceDiscountAmount,
    invoiceTaxAmount,
    remaining,
    attachments: payment.attachments,
  };
}

/** Screen + print — same layout as the document card on the detail page. */
export function DocumentPdfPreviewContent({
  document: doc,
  items,
  payments = [],
}: DocumentPdfPreviewContentProps) {
  const paymentRecord = buildReceiptPaymentRecord(doc, payments);

  return (
    <div
      id="document-pdf-content"
      className="mx-auto max-w-4xl print:max-w-none"
    >
      <QuotationInvoiceDocumentBody document={doc} items={items} paymentRecord={paymentRecord} />
    </div>
  );
}
